import { useMemo, useEffect, useReducer, useCallback } from 'react';
import axios, { endpoints } from '@/utils/api/axios';
import * as Sentry from '@sentry/react';
import { useQueryClient } from '@tanstack/react-query';
import { useRouter } from '@/routes/hooks';
import { paths } from '@/routes/paths';
import { AuthContext } from './auth-context';
import { setSession, isValidToken } from './utils';
import { AuthUserType, ActionMapType, AuthStateType } from '../../types';

// NOTE:
// We only build demo at basic level.
// Customer will need to do some extra handling yourself if you want to extend the logic and other features...

enum Types {
  INITIAL = 'INITIAL',
  LOGIN = 'LOGIN',
  REGISTER = 'REGISTER',
  LOGOUT = 'LOGOUT',
}

type Payload = {
  [Types.INITIAL]: {
    user: AuthUserType;
  };
  [Types.LOGIN]: {
    user: AuthUserType;
  };
  [Types.REGISTER]: {
    user: AuthUserType;
  };
  [Types.LOGOUT]: undefined;
};

type ActionsType = ActionMapType<Payload>[keyof ActionMapType<Payload>];

// ----------------------------------------------------------------------

const initialState: AuthStateType = {
  user: null,
  loading: true,
};

const reducer = (state: AuthStateType, action: ActionsType) => {
  if (action.type === Types.INITIAL) {
    return {
      loading: false,
      user: action.payload.user,
    };
  }
  if (action.type === Types.LOGIN) {
    return {
      ...state,
      user: action.payload.user,
    };
  }
  if (action.type === Types.REGISTER) {
    return {
      ...state,
      user: action.payload.user,
    };
  }
  if (action.type === Types.LOGOUT) {
    return {
      ...state,
      user: null,
    };
  }
  return state;
};

// ----------------------------------------------------------------------

const STORAGE_KEY = 'accessToken';

type Props = {
  children: React.ReactNode;
};

// Memoize the value key mapping to avoid recreating on every permission check
const VALUE_KEY_MAPPING: Record<string, string> = {
  view: 'canView',
  create: 'canCreate',
  update: 'canUpdate',
  delete: 'canDelete',
};

function checkPermission(
  user: AuthUserType,
  module: string,
  submodule: string,
  access: string,
  ownerID?: string
) {
  if (!user) return false;
  if (user.isAdmin) return true;

  const permissions = user.permissions?.[module]?.[submodule];
  if (!permissions) return false;

  const accessType = permissions[access];
  const accessValue = permissions[VALUE_KEY_MAPPING[access]];

  if (accessType === 'boolean') {
    if (ownerID !== undefined) {
      return ownerID === user._id;
    }
    return accessValue;
  }

  if (accessType === 'level') {
    if (accessValue === 'assigned' && ownerID !== undefined) {
      return ownerID === user._id;
    }

    if (accessValue === 'team' && ownerID !== undefined) {
      return user.teamMemberships?.includes(ownerID);
    }

    return accessValue !== 'none';
  }
  return access in permissions;
}

export function AuthProvider({ children }: Props) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const queryClient = useQueryClient();
  const router = useRouter();

  // Memoize the checkPermission function to avoid recreating on every render
  const createCheckPermission = useCallback(
    (user: AuthUserType) => (module: string, submodule: string, access: string, ownerID?: string) =>
      checkPermission(user, module, submodule, access, ownerID),
    []
  );

  // Memoize user data transformation to avoid recreating on every render
  const transformUserData = useCallback(
    (user: any, accessToken: string) => ({
      ...user,
      displayName: `${user?.firstName} ${user?.lastName}`,
      photoURL: user?.avatar?.url ? decodeURI(user?.avatar?.url) : '',
      accessToken,
      checkPermission: createCheckPermission(user),
    }),
    [createCheckPermission]
  );

  const initialize = useCallback(async () => {
    try {
      const accessToken = localStorage.getItem(STORAGE_KEY);

      if (accessToken && isValidToken(accessToken)) {
        setSession(accessToken);

        const res = await axios.get(endpoints.auth.me);
        const { user } = res.data;

        const transformedUser = transformUserData(user, accessToken);

        dispatch({
          type: Types.INITIAL,
          payload: {
            user: transformedUser,
          },
        });
      } else {
        dispatch({
          type: Types.INITIAL,
          payload: {
            user: null,
          },
        });
      }
    } catch (error) {
      Sentry.captureException(error);
      dispatch({
        type: Types.INITIAL,
        payload: {
          user: null,
        },
      });
    }
  }, [transformUserData]);

  useEffect(() => {
    initialize();
  }, [initialize]);

  // LOGIN
  const login = useCallback(
    async (username: string, password: string) => {
      const data = {
        username,
        password,
      };

      const res = await axios.post(endpoints.auth.login, data);
      const { accessToken, user } = res.data;

      setSession(accessToken);

      const transformedUser = transformUserData(user, accessToken);

      dispatch({
        type: Types.LOGIN,
        payload: {
          user: transformedUser,
        },
      });
    },
    [transformUserData]
  );

  // REGISTER
  const register = useCallback(
    async (email: string, password: string, firstName: string, lastName: string) => {
      const data = {
        email,
        password,
        firstName,
        lastName,
      };

      const res = await axios.post(endpoints.auth.register, data);
      const { accessToken, user } = res.data;

      localStorage.setItem(STORAGE_KEY, accessToken);

      const transformedUser = transformUserData(user, accessToken);

      dispatch({
        type: Types.REGISTER,
        payload: {
          user: transformedUser,
        },
      });
    },
    [transformUserData]
  );

  // LOGOUT
  const logout = useCallback(async () => {
    setSession(null);
    router.replace(paths.auth.jwt.login);
    queryClient.removeQueries();
    dispatch({
      type: Types.LOGOUT,
    });
  }, [queryClient, router]);

  // ----------------------------------------------------------------------

  // Memoize authentication status calculation
  const checkAuthenticated = useMemo(
    () => (state.user ? 'authenticated' : 'unauthenticated'),
    [state.user]
  );

  // Memoize loading status calculation
  const status = useMemo(
    () => (state.loading ? 'loading' : checkAuthenticated),
    [state.loading, checkAuthenticated]
  );

  // Memoize authentication boolean values
  const isAuthenticated = useMemo(() => status === 'authenticated', [status]);
  const isUnauthenticated = useMemo(() => status === 'unauthenticated', [status]);
  const isLoading = useMemo(() => status === 'loading', [status]);

  const memoizedValue = useMemo(
    () => ({
      user: state.user,
      method: 'jwt',
      loading: isLoading,
      authenticated: isAuthenticated,
      unauthenticated: isUnauthenticated,
      //
      initialize,
      login,
      register,
      logout,
    }),
    [state.user, isLoading, isAuthenticated, isUnauthenticated, initialize, login, register, logout]
  );

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}
